import config from '../config/config';

/**
 * Initial login - handles password authentication and 2FA initiation
 * @param {string} username - User's email
 * @param {string} password - User's password
 * @param {string|null} deviceToken - Optional trusted device token from localStorage
 * @returns {Promise<Object>} Response with status and data/error
 */
export const APILogon = async (username, password, deviceToken = null) => {
  try {
    const url = `${config.API_BASE_URL}${config.endpoints.signin}`;
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        username,
        password,
        ...(deviceToken && { deviceToken }) // Include deviceToken if provided
      })
    };

    const res = await fetch(url, requestOptions);

    if (res.status === 200) {
      const data = await res.json();

      // Return the full response to handle different scenarios:
      // 1. Success (trusted device): data.data will have token
      // 2. Requires 2FA: data.requires2FA will be true
      // 3. Requires setup: data.requires2FASetup will be true
      return data;
    } else {
      return { status: false, err: "Invalid Logon Details" };
    }
  } catch (err) {
    return { status: false, err: `Network error: ${err.message}` };
  }
};

/**
 * Verify 2FA code after initial login
 * @param {number} userId - User ID from login response
 * @param {string} code - 6-digit 2FA code
 * @param {string} challengeId - Challenge ID from login response
 * @param {boolean} trustDevice - Whether to trust this device for 30 days
 * @param {string|null} deviceName - Friendly name for trusted device
 * @returns {Promise<Object>} Response with JWT token if successful
 */
export const APIVerify2FA = async (userId, code, challengeId, trustDevice = false, deviceName = null) => {
  try {
    const url = `${config.API_BASE_URL}${config.endpoints.verify2FA}`;
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        userId,
        code,
        challengeId,
        trustDevice,
        ...(trustDevice && deviceName && { deviceName })
      })
    };

    const res = await fetch(url, requestOptions);
    const data = await res.json();

    return data;
  } catch (err) {
    return { status: false, err: `Network error: ${err.message}` };
  }
};

/**
 * Initiate 2FA setup for first-time users
 * @param {string} setupToken - Setup token from login response
 * @param {string} method - 2FA method: 'email', 'sms', or 'totp'
 * @param {string|null} phone - Phone number (required for SMS method)
 * @returns {Promise<Object>} Response with challengeId or QR code info
 */
export const APISetup2FA = async (setupToken, method, phone = null) => {
  try {
    const url = `${config.API_BASE_URL}${config.endpoints.setup2FA}`;
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        setupToken,
        method,
        ...(method === 'sms' && phone && { phone })
      })
    };

    const res = await fetch(url, requestOptions);
    const data = await res.json();

    return data;
  } catch (err) {
    return { status: false, err: `Network error: ${err.message}` };
  }
};

/**
 * Verify 2FA setup code
 * @param {string} setupToken - Setup token from login response
 * @param {string} code - 6-digit verification code
 * @param {string|null} challengeId - Challenge ID (not needed for TOTP)
 * @returns {Promise<Object>} Response with JWT token and backup codes
 */
export const APIVerifySetup2FA = async (setupToken, code, challengeId = null) => {
  try {
    const url = `${config.API_BASE_URL}${config.endpoints.verifySetup2FA}`;
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        setupToken,
        code,
        ...(challengeId && { challengeId })
      })
    };

    const res = await fetch(url, requestOptions);
    const data = await res.json();

    return data;
  } catch (err) {
    return { status: false, err: `Network error: ${err.message}` };
  }
};
